export function phaseToStatus(phase, jobStatus) {
  if (phase === 'DONE' && jobStatus === 'FAILED') return 'failed'

  const map = {
    PLANNING: 'planning',
    AWAITING_INPUT: 'awaiting_input',
    PENDING_APPROVAL: 'pending_approval',
    APPROVED: 'approved',
    EXECUTING: 'running',
    DELIVERING: 'delivering',
    WAITING_CREDENTIALS: 'waiting_credentials',
    SYNTHESIZING: 'synthesizing',
    DONE: 'completed',
    ERRORED: 'failed',
    CANCELLED: 'cancelled',
  }

  return map[phase] ?? phase?.toLowerCase() ?? 'unknown'
}

export function statusToTone(status) {
  const map = {
    planning: 'neutral',
    awaiting_input: 'pending',
    pending_approval: 'pending',
    approved: 'approved',
    running: 'active',
    delivering: 'active',
    waiting_credentials: 'pending',
    synthesizing: 'active',
    completed: 'ready',
    failed: 'failed',
    cancelled: 'neutral',
  }

  return map[status] ?? 'neutral'
}

export function formatJobStatus(status) {
  if (!status) return ''
  return status.replace(/_/g, ' ')
}

export function normalizeJob(raw) {
  const status = phaseToStatus(raw.phase, raw.jobStatus)

  return {
    ...raw,
    jobId: raw.id,
    status,
    statusLabel: formatJobStatus(status),
    tone: statusToTone(status),
    updatedAt: raw.completedAt ?? raw.planClaimedAt ?? raw.synthesisClaimedAt ?? raw.createdAt,
  }
}

export function summarizeJobTitle(description, fallback, maxLength = 88) {
  if (typeof description !== 'string' || !description.trim()) {
    return fallback
  }

  // Try to extract a clean title from markdown content
  const lines = description.split('\n').map((line) => line.trim()).filter(Boolean)

  for (const line of lines) {
    // Skip empty lines and horizontal rules
    if (!line || /^[-*_]{3,}$/.test(line)) continue

    // Extract text from markdown heading (# Title)
    const headingMatch = line.match(/^#{1,6}\s+(.+)$/)
    if (headingMatch) {
      return truncateTitle(headingMatch[1], maxLength)
    }

    // Skip lines that look like metadata or code
    if (/^```/.test(line) || /^>/.test(line)) continue

    // Use first non-heading content line, but clean it up
    let title = line
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold
      .replace(/\*([^*]+)\*/g, '$1')       // Remove italic
      .replace(/`([^`]+)`/g, '$1')         // Remove inline code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Remove links, keep text
      .trim()

    if (title) {
      // If it's a very long line (paragraph), take first sentence
      if (title.length > maxLength * 1.5) {
        const sentenceEnd = title.search(/[.!?]\s/)
        if (sentenceEnd > 0 && sentenceEnd < maxLength) {
          title = title.slice(0, sentenceEnd + 1)
        }
      }
      return truncateTitle(title, maxLength)
    }
  }

  return fallback
}

function truncateTitle(text, maxLength) {
  if (text.length <= maxLength) return text
  const sliceLength = Math.max(1, maxLength - 3)
  return `${text.slice(0, sliceLength)}...`
}

export function isActiveJobStatus(status) {
  return [
    'planning',
    'awaiting_input',
    'pending_approval',
    'approved',
    'running',
    'delivering',
    'waiting_credentials',
    'synthesizing',
  ].includes(status)
}

export function needsOperatorAttention(status) {
  return ['awaiting_input', 'pending_approval', 'waiting_credentials'].includes(status)
}
